import { useEffect, useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import { Box, Grid, Typography, useTheme } from '@mui/material';

import { showErrorToast } from 'src/components/Toasts/toast';
import { DEFAULT_BORDER } from 'src/constants';
import { useCountries } from 'src/hooks';
import { IUserDetailsProps } from 'src/types/edit-user';
import { extractCallingCodeAndNumber } from 'src/utils/extractCallingCode';

const FlagIcon = ({ src }) => {
    return (
        <span
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            {src && (
                <img
                    src={src}
                    alt="country flag"
                    style={{
                        borderRadius: '50%',
                        height: '24px',
                        width: '24px',
                        objectFit: 'cover',
                    }}
                />
            )}
        </span>
    );
};

const UserDetailsCard = ({
    firstName,
    lastName,
    email,
    role,
    phoneNumber,
    username,
    openUserDetailsForm,
}: IUserDetailsProps) => {
    const { countries } = useCountries();

    const [flag, setFlag] = useState('');
    const theme = useTheme();

    // Once the countries have been fetched, extract the calling code from the users mobile number & find their respective flag
    useEffect(() => {
        if (phoneNumber) {
            try {
                const { callingCode } = extractCallingCodeAndNumber(phoneNumber);

                const inputNumberCountryFlag = countries?.find(
                    (country) => country.callingCode === callingCode
                )?.flag;

                setFlag(inputNumberCountryFlag);
            } catch (error) {
                showErrorToast('This users phone number is invalid');
            }
        }
    }, [phoneNumber]);

    return (
        <Grid
            item
            xs={12}
            sx={{
                ...DEFAULT_BORDER,
                backgroundColor: '#FFFFFF',
                boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                padding: '32px 24px',
            }}
            container
        >
            <Grid item xs={12} spacing={3} container>
                <Grid
                    item
                    xs={12}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ borderBottom: '1px solid #F18F34' }}
                >
                    <Box display="flex" flexDirection="column" gap="8px" marginBottom="8px">
                        <Typography variant="h2">User Details</Typography>
                        <Typography>Add new user details in this section</Typography>
                    </Box>
                    <EditIcon onClick={openUserDetailsForm} />
                </Grid>
                <Grid item lg={6} xs={12} display="flex" flexDirection="column" gap="24px">
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                        }}
                        color={theme.palette.dark.main}
                    >
                        First name
                    </Typography>

                    <Typography sx={{ fontSize: '20px', fontWeight: 400 }}>{firstName}</Typography>
                </Grid>
                <Grid item lg={6} xs={12} display="flex" flexDirection="column" gap="24px">
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                        }}
                        color={theme.palette.dark.main}
                    >
                        Surname
                    </Typography>

                    <Typography sx={{ fontSize: '20px', fontWeight: 400 }}>{lastName}</Typography>
                </Grid>

                <Grid item lg={6} xs={12} display="flex" flexDirection="column" gap="24px">
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                        }}
                        color={theme.palette.dark.main}
                    >
                        Mobile Number
                    </Typography>
                    <Box display="flex" flexDirection="row" gap="8px">
                        {flag && <FlagIcon src={flag} />}
                        <Typography sx={{ fontSize: '20px', fontWeight: 400 }}>
                            {phoneNumber}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item lg={6} xs={12} display="flex" flexDirection="column" gap="24px">
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                        }}
                        color={theme.palette.dark.main}
                    >
                        Email Address
                    </Typography>

                    <Typography sx={{ fontSize: '20px', fontWeight: 400 }}>{email}</Typography>
                </Grid>
                <Grid item lg={6} xs={12} display="flex" flexDirection="column" gap="24px">
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                        }}
                        color={theme.palette.dark.main}
                    >
                        User Role
                    </Typography>

                    <Typography sx={{ fontSize: '20px', fontWeight: 400 }}>{role}</Typography>
                </Grid>
                <Grid item lg={6} xs={12} display="flex" flexDirection="column" gap="24px">
                    <Typography
                        sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                        }}
                        color={theme.palette.dark.main}
                    >
                        Username
                    </Typography>

                    <Typography sx={{ fontSize: '20px', fontWeight: 400 }}>{username}</Typography>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default UserDetailsCard;
